import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Pencil, Trash2, X, Save } from "lucide-react";
import { Card, Btn, Badge, Avatar, EmptyState, statusTone, SearchInput } from "@/components/ui-kit";
import api, { docFileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RecordDetailModal from "@/components/RecordDetailModal";

const GENDERS = ["Male", "Female", "Other"];
const STATUSES = ["New", "On Track", "Review Needed", "At Risk", "Completed", "Discharged"];
const EMPTY = { name: "", age: "", gender: "Male", dob: "", phone: "", email: "", facility: "", diagnosis: "", conditions: "", allergies: "", status: "New" };

export default function AdminPatients() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // patient being edited
  const [view, setView] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/patients", { params: q ? { q } : {} }).then((r) => setPatients(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openEdit = (p) => {
    setForm({
      name: p.name || "", age: p.age ?? "", gender: p.gender || "Male", dob: p.dob || "",
      phone: p.phone || "", email: p.email || "", facility: p.facility || "",
      diagnosis: p.diagnosis || "", conditions: p.conditions || "", allergies: p.allergies || "",
      status: p.status || "New",
    });
    setEditing(p);
  };

  const saveEdit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put(`/admin/patients/${editing.id}`, form);
      toast.success("Patient updated"); setEditing(null); load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Update failed"); }
    setSaving(false);
  };

  const del = async (p) => {
    const override = user?.role === "superadmin";
    const msg = override
      ? `Delete ${p.name} (${p.pranit_id}) from the app? Active-order checks are bypassed; the record is removed from every screen but retained in the database.`
      : `Permanently delete ${p.name} (${p.pranit_id})? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/patients/${p.id}`);
      toast.success(override ? "Patient removed from the app — record retained in the database" : "Patient deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Card data-testid="admin-patients">
      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4 border-b border-slate-100 flex-wrap">
        <h3 className="font-display font-semibold flex items-center gap-2"><Users size={17} className="text-teal-600" /> Patients</h3>
        <SearchInput testid="admin-patient-search" value={q} onChange={setQ} placeholder="Search name, ID, diagnosis…" className="sm:w-72" />
      </div>
      {patients.length === 0 ? (
        <EmptyState icon={<Users size={24} />} title="No patients found"
          subtitle="Patients appear here once doctors create treatment orders. Try clearing your search." />
      ) : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Patient", "ID", "Diagnosis", "Doctor", "Orders", "Status", ""].map((h) => (
            <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>))}</tr></thead>
          <tbody>
            {patients.map((p) => (
              <tr key={p.id} onClick={() => setView(p)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" data-testid={`admin-patient-row-${p.id}`}>
                <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar name={p.name} size={32} /><div><div className="font-semibold text-[13px]">{p.name}</div><div className="text-[11.5px] text-slate-400">{p.age ?? "—"} · {p.gender}{p.phone ? ` · ${p.phone}` : ""}</div></div></div></td>
                <td className="px-4 py-3 metric text-[12px] text-slate-500">{p.pranit_id}</td>
                <td className="px-4 py-3 text-[13px]">{p.diagnosis || "—"}</td>
                <td className="px-4 py-3 text-[13px] text-slate-600"><div className="flex items-center gap-2">{p.doctor_name ? <Avatar name={p.doctor_name} size={24} src={docFileUrl(p.doctor_photo)} /> : null}<span>{p.doctor_name || "—"}</span></div></td>
                <td className="px-4 py-3 text-[13px] metric">{p.active_orders}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(p.status)}>{p.status}</Badge></td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><div className="flex gap-1.5 justify-end">
                  <Btn size="sm" onClick={() => openEdit(p)} data-testid={`admin-patient-edit-${p.id}`}><Pencil size={13} /> Edit</Btn>
                  <Btn size="sm" variant="danger" onClick={() => del(p)} data-testid={`admin-patient-delete-${p.id}`}><Trash2 size={13} /></Btn>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {view && <RecordDetailModal
        title={view.name}
        subtitle={`${view.pranit_id || "—"} · ${view.age ?? "—"} · ${view.gender || "—"}`}
        badge={<Badge tone={statusTone(view.status)}>{view.status}</Badge>}
        onClose={() => setView(null)}
        sections={[
          { heading: "Contact", fields: [
            { label: "Phone", value: view.phone, testid: "admin-view-phone" },
            { label: "Email", value: view.email },
            { label: "Date of birth", value: view.dob },
            { label: "Facility", value: view.facility },
          ] },
          { heading: "Care", fields: [
            { label: "Owning doctor", value: view.doctor_name, testid: "admin-view-doctor" },
            { label: "Active orders", value: view.active_orders },
            { label: "Diagnosis", value: view.diagnosis, full: true },
            { label: "Conditions", value: view.conditions, full: true },
            { label: "Allergies", value: view.allergies, full: true },
          ] },
        ]} />}

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="admin-patient-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-[15px]">Edit patient — {editing.pranit_id}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700" data-testid="admin-patient-edit-close"><X size={18} /></button>
            </div>
            <form onSubmit={saveEdit} className="grid grid-cols-2 gap-3">
              <label className="block col-span-2"><span className="pq-label block mb-1">Full name</span><input required className="pq-input" value={form.name} onChange={set("name")} data-testid="edit-patient-name" /></label>
              <label className="block"><span className="pq-label block mb-1">Age</span><input type="number" className="pq-input" value={form.age} onChange={set("age")} data-testid="edit-patient-age" /></label>
              <label className="block"><span className="pq-label block mb-1">Gender</span><select className="pq-input" value={form.gender} onChange={set("gender")} data-testid="edit-patient-gender">{GENDERS.map((g) => <option key={g}>{g}</option>)}</select></label>
              <label className="block"><span className="pq-label block mb-1">Date of birth</span><input className="pq-input" value={form.dob} onChange={set("dob")} placeholder="YYYY-MM-DD" data-testid="edit-patient-dob" /></label>
              <label className="block"><span className="pq-label block mb-1">Phone</span><input className="pq-input" value={form.phone} onChange={set("phone")} data-testid="edit-patient-phone" /></label>
              <label className="block col-span-2"><span className="pq-label block mb-1">Email</span><input type="email" className="pq-input" value={form.email} onChange={set("email")} data-testid="edit-patient-email" /></label>
              <label className="block col-span-2"><span className="pq-label block mb-1">Facility</span><input className="pq-input" value={form.facility} onChange={set("facility")} data-testid="edit-patient-facility" /></label>
              <label className="block col-span-2"><span className="pq-label block mb-1">Diagnosis</span><input className="pq-input" value={form.diagnosis} onChange={set("diagnosis")} data-testid="edit-patient-diagnosis" /></label>
              <label className="block col-span-2"><span className="pq-label block mb-1">Conditions</span><input className="pq-input" value={form.conditions} onChange={set("conditions")} data-testid="edit-patient-conditions" /></label>
              <label className="block col-span-2"><span className="pq-label block mb-1">Allergies</span><input className="pq-input" value={form.allergies} onChange={set("allergies")} data-testid="edit-patient-allergies" /></label>
              <label className="block col-span-2"><span className="pq-label block mb-1">Status</span><select className="pq-input" value={form.status} onChange={set("status")} data-testid="edit-patient-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
              <div className="col-span-2 flex justify-end gap-2 mt-1">
                <Btn type="button" onClick={() => setEditing(null)} data-testid="edit-patient-cancel">Cancel</Btn>
                <Btn type="submit" variant="primary" disabled={saving} data-testid="edit-patient-save"><Save size={14} /> {saving ? "Saving…" : "Save changes"}</Btn>
              </div>
            </form>
          </div>
        </div>
      )}
    </Card>
  );
}
